import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd"
import { useNavigate } from "react-router-dom"
import { Contract, ContractStatus, QuotaUnit } from "../store/types"
import { EntitlementFields, formToEntitlement } from "./entitlement-fields"

const STATUS_COLOR: Record<ContractStatus, string> = {
  DRAFT: "default",
  ACTIVE: "green",
  SUSPENDED: "gold",
  CLOSED: "red",
}

function quotaLabel(c: Contract): string {
  if (c.quotaUnit === "SECONDS") {
    return c.maxSeconds ? `${Math.round(Number(c.maxSeconds) / 3600)} h` : "—"
  }

  return c.maxItems !== null ? `${c.maxItems} items` : "—"
}

export const ContractsList: FC = observer(() => {
  const { dataStore: { dataDeliveryStore } } = useStore()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()

  useEffect(() => {
    dataDeliveryStore.loadContracts()
    dataDeliveryStore.loadGames()
    dataDeliveryStore.loadFacets()
  }, [dataDeliveryStore])

  const onCreate = async() => {
    // validateFields() rejects on validation failure (e.g. empty name); antd already
    // renders the inline errors, so swallow the rejection instead of crashing the app.
    let v
    try {
      v = await form.validateFields()
    } catch {
      return
    }
    const created = await dataDeliveryStore.createContract({
      name: v.name,
      quotaUnit: v.quotaUnit,
      maxItems: v.quotaUnit === "ITEMS" ? v.quotaValue : undefined,
      maxSeconds: v.quotaUnit === "SECONDS" ? v.quotaValue * 3600 : undefined,
      customerKey: v.customerKey,
      // Always exactly one row: a contract with zero entitlement rows can
      // deliver nothing, and an all-axes-unset row is the "any" allow-list.
      entitlements: [formToEntitlement(v)],
    })
    if (created) {
      setOpen(false)
      form.resetFields()
      navigate(`/data-delivery/contracts/${created.id}`)
    }
  }

  const unit: QuotaUnit = Form.useWatch("quotaUnit", form) ?? "ITEMS"

  const columns = [
    {
      title: "Contract",
      dataIndex: "id",
      key: "id",
      render: (id: string) => (
        <Space size={4}>
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/data-delivery/contracts/${id}`)}>
            {id.slice(0, 8)}…
          </Button>
          <Typography.Text copyable={{ text: id }} />
        </Space>
      ),
    },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Client",
      dataIndex: "clientId",
      key: "clientId",
      render: (v: string) => (
        <Space size={4}>
          <span>{v.slice(0, 8)}…</span>
          <Typography.Text copyable={{ text: v }} />
        </Space>
      ),
    },
    { title: "Quota", key: "quota", render: (_: unknown, c: Contract) => quotaLabel(c) },
    { title: "Customer key", dataIndex: "customerKey", key: "customerKey", render: (v: string) => v ?? "—" },
    { title: "Bucket", dataIndex: "deliveryBucket", key: "deliveryBucket", render: (v: string | null) => v ?? "—" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: ContractStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
  ]

  return (
    <Card
      title="Delivery Contracts"
      extra={<Button type="primary" onClick={() => setOpen(true)}>New contract</Button>}
    >
      <Table rowKey="id" columns={columns} dataSource={dataDeliveryStore.contracts} loading={dataDeliveryStore.isLoading} />

      <Modal title="New contract" open={open} onOk={onCreate} onCancel={() => setOpen(false)} okText="Create" width={560}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ quotaUnit: "ITEMS" as QuotaUnit, quotaValue: 1000 }}
        >
          <Form.Item name="name" label="Customer name" rules={[{ required: true }]}>
            <Input placeholder="Acme Data Co" />
          </Form.Item>

          <Space size="large">
            <Form.Item name="quotaUnit" label="Quota type">
              <Select
                style={{ width: 160 }}
                options={[{ value: "ITEMS", label: "Items (count)" }, { value: "SECONDS", label: "Hours (duration)" }]}
              />
            </Form.Item>
            <Form.Item name="quotaValue" label={unit === "SECONDS" ? "Hours" : "Items"} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: 180 }} />
            </Form.Item>
          </Space>

          <Form.Item
            name="customerKey"
            label="Customer key"
            extra="Bucket is derived server-side as <prefix>-<region>-<customerKey> (must match a provisioned cust-s3 bucket)."
            rules={[
              { required: true },
              {
                pattern: /^[a-z0-9][a-z0-9-]{1,40}$/,
                message: "Lowercase DNS-safe token (a-z, 0-9, -), e.g. customer1",
              },
            ]}
          >
            <Input placeholder="customer1" />
          </Form.Item>

          <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>Entitlement filters (optional)</div>
          <EntitlementFields />
        </Form>
      </Modal>
    </Card>
  )
})
